import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { registerDataValidationErrorMatcher } from 'errors/dataValidationError';
import MoveIcon from 'images/v5/icons/copy-07.svg';
import MoveFolderIcon from 'images/v5/icons/folder-plus.svg';
import Button from 'layout/atoms/Button/Button';
import Label from 'layout/atoms/Label';
import Loader from 'layout/atoms/Loader/Loader';
import Modal from 'layout/atoms/Modal/Modal';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import FoldersTreeMove from 'layout/organisms/FoldersTreeMove/FoldersTreeMove';
import { closeModal, selectLayout } from 'store/layoutSlice';
import { resetFoldersTree, setRefetchFlag as setRefetchFlagFolders, } from 'store/specificationsFoldersSlice';
import { fetchRootFolder, fetchRootFolders, fetchSpecificationsFolders, moveFolder, moveSpecification, resetFoldersTreeMove, resetSpecificationsFolders, selectCurrentFolder, selectFoldersTree, selectMoveFolder, selectRootFolder, } from 'store/specificationsMoveSlice';
import { setRefetchFlag } from 'store/specificationsSlice';
import { addToast } from 'store/toastsSlice';
function MoveItem({ folder = false }) {
    const dispatch = useDispatch();
    const currentFolder = useSelector(selectCurrentFolder);
    const { t } = useTranslation(['specification', 'exceptions', 'emptyStates']);
    const status = useSelector((state) => state.specificationsMove.status.fetch);
    const refetch = useSelector((state) => state.specificationsMove.refetchFlags.fetchList);
    const moveStatus = useSelector((state) => state.specificationsMove.status.move);
    const foldersTree = useSelector(selectFoldersTree);
    const rootFolder = useSelector(selectRootFolder);
    const moveToFolder = useSelector(selectMoveFolder);
    const itemToMove = useSelector((state) => {
        var _a;
        return (_a = selectLayout(state).showModals.find((m) => folder ? m.name === 'moveFolder' : m.name === 'moveSpecification')) === null || _a === void 0 ? void 0 : _a.data.id;
    });
    useEffect(() => {
        dispatch(fetchRootFolder());
    }, [dispatch]);
    useEffect(() => {
        if (rootFolder.rootFolderId !== null) {
            dispatch(fetchRootFolders({ folderId: rootFolder.rootFolderId }));
        }
    }, [dispatch, rootFolder.status, rootFolder.rootFolderId]);
    useEffect(() => {
        if (refetch && rootFolder.rootFolderId !== null) {
            dispatch(fetchRootFolders({ folderId: rootFolder.rootFolderId }));
        }
        if (currentFolder !== '') {
            dispatch(fetchSpecificationsFolders({ folderId: currentFolder }));
        }
    }, [dispatch, rootFolder.rootFolderId, refetch, currentFolder]);
    useEffect(() => {
        if (currentFolder !== '') {
            dispatch(fetchSpecificationsFolders({ folderId: currentFolder }));
        }
    }, [dispatch, currentFolder]);
    useEffect(() => {
        return () => {
            dispatch(resetSpecificationsFolders());
        };
    }, [dispatch]);
    registerDataValidationErrorMatcher();
    const handleSubmit = () => {
        if (folder) {
            dispatch(moveFolder({ folderToMoveId: itemToMove, destinationFolderId: moveToFolder }))
                .unwrap()
                .then(() => {
                dispatch(closeModal({ name: 'moveFolder' }));
                dispatch(setRefetchFlag({ flag: 'fetch', value: true }));
                dispatch(setRefetchFlagFolders({ flag: 'fetch', value: true }));
                dispatch(resetFoldersTree());
                dispatch(resetFoldersTreeMove());
            })
                .catch((exceptionCode) => dispatch(addToast({ color: 'negative', title: t(`exceptions:${exceptionCode}`) })));
        }
        else {
            dispatch(moveSpecification({
                modelSpecificationId: itemToMove,
                modelSpecificationFolderId: moveToFolder,
            }))
                .unwrap()
                .then(() => {
                dispatch(closeModal({ name: 'moveSpecification' }));
                dispatch(setRefetchFlag({ flag: 'fetch', value: true }));
                dispatch(setRefetchFlagFolders({ flag: 'fetch', value: true }));
            })
                .catch((exceptionCode) => dispatch(addToast({ color: 'negative', title: t(`exceptions:${exceptionCode}`) })));
        }
    };
    return (React.createElement(Modal, { alwaysScroll: true, name: folder ? 'moveFolder' : 'moveSpecification', size: "s", header: React.createElement(Label, { weight: "strong" },
            folder ? React.createElement(MoveFolderIcon, null) : React.createElement(MoveIcon, null),
            " ",
            t('move')), footer: React.createElement(Button, { color: "primary", disabled: moveToFolder === '', onClick: handleSubmit, loading: moveStatus === 'loading' }, t('move')) },
        status === 'succeeded' && foldersTree.length === 0 && (React.createElement(EmptyState, { header: t('common:no_results_header'), subheader: t('common:no_results_subheader') })),
        foldersTree.length > 0 && (React.createElement(FoldersTreeMove, { rootFolderId: rootFolder.rootFolderId, folders: foldersTree, itemToMove: itemToMove })),
        (status === 'loading' || status === 'idle') && React.createElement(Loader, { overlay: true })));
}
export default memo(MoveItem);
